import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';



const CertificateTable = ({ certificates, onViewDetails, onGenerateReminder, onUploadDocument }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'expiryDate', direction: 'asc' });

  const getStatusColor = (daysUntilExpiry) => {
    if (daysUntilExpiry < 0 || daysUntilExpiry <= 14) return 'var(--color-error)';
    if (daysUntilExpiry <= 30) return 'var(--color-warning)';
    return 'var(--color-success)';
  };

  const getStatusLabel = (daysUntilExpiry) => {
    if (daysUntilExpiry < 0) return 'Expired';
    if (daysUntilExpiry <= 14) return 'Critical';
    if (daysUntilExpiry <= 30) return 'Expiring Soon';
    return 'Valid';
  };

  const getCertificateIcon = (type) => {
    const icons = {
      gas: 'Flame',
      epc: 'Zap',
      eicr: 'Plug',
      legionella: 'Droplet',
      smoke: 'AlertTriangle',
    };
    return icons?.[type] || 'FileText';
  };

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig?.key === key && sortConfig?.direction === 'asc' ? 'desc' : 'asc',
    });
  };

  const sortedCertificates = [...certificates]?.sort((a, b) => {
    if (sortConfig?.key === 'expiryDate') {
      const dateA = new Date(a.expiryDate);
      const dateB = new Date(b.expiryDate);
      return sortConfig?.direction === 'asc' ? dateA - dateB : dateB - dateA;
    }
    if (sortConfig?.key === 'propertyId') {
      return sortConfig?.direction === 'asc'
        ? a?.propertyId?.localeCompare(b?.propertyId)
        : b?.propertyId?.localeCompare(a?.propertyId);
    }
    return 0;
  });

  return (
    <div className="card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left p-4 text-sm font-medium text-muted-foreground font-caption">
                <button
                  onClick={() => handleSort('propertyId')}
                  className="flex items-center gap-2 hover:text-foreground transition-smooth"
                >
                  Property ID
                  <Icon
                    name={sortConfig?.key === 'propertyId' && sortConfig?.direction === 'desc' ? 'ChevronDown' : 'ChevronUp'}
                    size={16}
                  />
                </button>
              </th>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground font-caption">Address</th>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground font-caption">Certificate Type</th>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground font-caption">Issue Date</th>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground font-caption">
                <button
                  onClick={() => handleSort('expiryDate')}
                  className="flex items-center gap-2 hover:text-foreground transition-smooth"
                >
                  Expiry Date
                  <Icon
                    name={sortConfig?.key === 'expiryDate' && sortConfig?.direction === 'desc' ? 'ChevronDown' : 'ChevronUp'}
                    size={16}
                  />
                </button>
              </th>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground font-caption">Status</th>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground font-caption">Documents</th>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground font-caption">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedCertificates?.map((cert) => (
              <tr key={cert?.id} className="table-row hover:bg-muted/5 transition-smooth">
                <td className="p-4">
                  <span className="font-medium text-foreground font-data">{cert?.propertyId}</span>
                </td>
                <td className="p-4">
                  <p className="text-sm text-foreground line-clamp-2">{cert?.address}</p>
                </td>
                <td className="p-4">
                  <div className="flex items-center gap-2">
                    <div className="h-8 w-8 rounded-lg bg-accent/10 flex items-center justify-center">
                      <Icon name={getCertificateIcon(cert?.type)} size={16} color="var(--color-accent)" />
                    </div>
                    <span className="text-sm text-foreground">{cert?.typeName}</span>
                  </div>
                </td>
                <td className="p-4">
                  <span className="text-sm text-muted-foreground font-data">{cert?.issueDate}</span>
                </td>
                <td className="p-4">
                  <span className="text-sm text-foreground font-data">{cert?.expiryDate}</span>
                </td>
                <td className="p-4">
                  <div className="flex items-center gap-2">
                    <div
                      className="h-2 w-2 rounded-full"
                      style={{ backgroundColor: getStatusColor(cert?.daysUntilExpiry) }}
                    />
                    <span
                      className="text-sm font-medium"
                      style={{ color: getStatusColor(cert?.daysUntilExpiry) }}
                    >
                      {getStatusLabel(cert?.daysUntilExpiry)}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      ({cert?.daysUntilExpiry < 0 ? 'Overdue' : `${cert?.daysUntilExpiry} days`})
                    </span>
                  </div>
                </td>
                <td className="p-4">
                  {cert?.hasDocument ? (
                    <button
                      onClick={() => onViewDetails(cert)}
                      className="flex items-center gap-2 text-sm text-accent hover:underline"
                    >
                      <Icon name="FileText" size={16} />
                      View PDF
                    </button>
                  ) : (
                    <button
                      onClick={() => onUploadDocument(cert)}
                      className="flex items-center gap-2 text-sm text-muted-foreground hover:text-accent"
                    >
                      <Icon name="Upload" size={16} />
                      Upload
                    </button>
                  )}
                </td>
                <td className="p-4">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => onViewDetails(cert)}
                      className="h-8 w-8 rounded-lg bg-input border border-border flex items-center justify-center transition-smooth hover:bg-muted"
                      aria-label="View details"
                    >
                      <Icon name="Eye" size={16} color="var(--color-muted-foreground)" />
                    </button>
                    <button
                      onClick={() => onGenerateReminder(cert)}
                      className="h-8 w-8 rounded-lg bg-input border border-border flex items-center justify-center transition-smooth hover:bg-muted"
                      aria-label="Generate reminder"
                    >
                      <Icon name="Mail" size={16} color="var(--color-muted-foreground)" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {certificates?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="FileX" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
          <p className="text-muted-foreground">No certificates found matching your filters</p>
        </div>
      )}
    </div>
  );
};

export default CertificateTable;